// The Leaflet map (plan.md §4.1, §8).
//
// Leaflet is imperative and owns its own DOM; React expects to own what it
// renders. The boundary is kept explicit, exactly as §8 asks:
//
//   - the map is created once, in an effect with an empty dependency array,
//     against a ref'd container React never re-renders;
//   - the map instance and layers live in refs, never in state (state would
//     re-render on every pan);
//   - marker updates are a diff applied to a Leaflet layer group, not
//     rendered children;
//   - React owns everything AROUND the map — panels, pickers, filters.
//
// react-leaflet is deliberately not used: it fights custom marker styling,
// which is most of what happens here (heat, fade, and the soft radius that
// snapped reports must render with).

import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { iconFor } from '../lib/icons.js';
import { i18n, getActiveLocale } from '../lib/i18n.js';
import { localizeCategoryName } from '../lib/taxonomy-i18n.js';

const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

/**
 * Visual weight from recency and corroboration count (plan.md §4.1).
 * Returns 0-1; drives size and opacity together so a fresh, well-corroborated
 * report reads as "louder" without needing a legend.
 */
function heatOf(report) {
  const ageHours = (Date.now() - new Date(report.created_at).getTime()) / 3_600_000;
  const recency = Math.max(0, 1 - ageHours / (24 * 7));
  const support = Math.min(1, (report.corroboration_count ?? 0) / 8);
  return Math.min(1, 0.45 * recency + 0.55 * support);
}

/** Reports fade as they approach expires_at (plan.md §4.1). */
function fadeOf(report) {
  const remaining = new Date(report.expires_at).getTime() - Date.now();
  if (remaining <= 0) return 0.25;
  const hours = remaining / 3_600_000;
  return Math.max(0.35, Math.min(1, hours / 12));
}

function markerIcon(report, isSelected) {
  const heat = heatOf(report);
  const fade = fadeOf(report);
  const size = Math.round(30 + heat * 16);
  const emoji = iconFor(report.category_icon, report.collection_slug);
  const count = report.corroboration_count ?? 0;

  return L.divIcon({
    className: 'report-marker-wrapper',
    html: `
      <div class="report-marker${isSelected ? ' is-selected' : ''}"
           style="--size:${size}px; --heat:${heat.toFixed(2)}; --fade:${fade.toFixed(2)}">
        <span class="report-marker__icon" aria-hidden="true">${emoji}</span>
        ${count > 0 ? `<span class="report-marker__count">${count > 99 ? '99+' : count}</span>` : ''}
      </div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

export default function MapView({
  reports,
  selectedId,
  onSelectReport,
  onBoundsChange,
  initialCenter,
  initialZoom = 14,
  flyTo,
  draftPin,
  pinDrop,
  onPinDropMove,
  onConfirmPinDrop,
  onCancelPinDrop,
  onMapClick,
  onDraftMove,
  mapRef,
}) {
  const containerRef = useRef(null);
  const map = useRef(null);
  const clusterGroup = useRef(null);
  const precisionLayer = useRef(null);
  const markers = useRef(new Map());   // report id -> { marker, report }
  const draftMarker = useRef(null);
  const draftCircle = useRef(null);
  const pinDropMarker = useRef(null);

  // Callbacks change every render; keep them in refs so the map's own
  // listeners can stay attached for the life of the component.
  const handlers = useRef({});
  handlers.current = {
    onBoundsChange, onSelectReport, onDraftMove,
    onMapClick, onPinDropMove, onConfirmPinDrop, onCancelPinDrop,
  };

  // --- create the map exactly once -----------------------------------------
  useEffect(() => {
    if (map.current || !containerRef.current) return;

    const instance = L.map(containerRef.current, {
      center: initialCenter ? [initialCenter.lat, initialCenter.lng] : [51.5074, -0.1278],
      zoom: initialZoom,
      zoomControl: false,
      // The detail panel occupies the bottom of the screen on mobile; keeping
      // attribution compact stops it colliding.
      attributionControl: true,
      tap: true,
    });

    L.tileLayer(TILE_URL, {
      attribution: TILE_ATTRIBUTION,
      maxZoom: 19,
      detectRetina: true,
    }).addTo(instance);

    L.control.zoom({ position: 'topright' }).addTo(instance);
    L.control.scale({ position: 'bottomleft', imperial: false }).addTo(instance);

    // Soft radii for snapped reports sit BELOW the markers, in their own pane.
    instance.createPane('precision');
    instance.getPane('precision').style.zIndex = 380;
    precisionLayer.current = L.layerGroup([], { pane: 'precision' }).addTo(instance);

    clusterGroup.current = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 30,
      // Reports snapped to the same grid cell stack exactly (plan.md OQ7), so
      // coincident markers need deliberate handling: spiderfy at max zoom
      // rather than leaving them permanently hidden under one another.
      spiderfyOnMaxZoom: true,
      disableClusteringAtZoom: 18,
      iconCreateFunction(cluster) {
        const n = cluster.getChildCount();
        const size = n < 10 ? 38 : n < 50 ? 46 : 54;
        return L.divIcon({
          html: `<div class="cluster-marker" style="--size:${size}px">${n}</div>`,
          className: 'cluster-marker-wrapper',
          iconSize: [size, size],
        });
      },
    }).addTo(instance);

    const emitBounds = () => {
      const b = instance.getBounds();
      handlers.current.onBoundsChange?.({
        south: b.getSouth(), west: b.getWest(),
        north: b.getNorth(), east: b.getEast(),
        zoom: instance.getZoom(),
      });
    };

    // Debounced so a drag is one request, not one per frame (plan.md §4.1).
    let timer = null;
    const debouncedEmit = () => {
      clearTimeout(timer);
      timer = setTimeout(emitBounds, 350);
    };
    instance.on('moveend zoomend', debouncedEmit);

    // Tapping empty map either relocates the draft pin (composer open),
    // drops/moves the pending pin (tap-to-place flow), or closes any open
    // report panel.  Drag events never arrive here -- Leaflet distinguishes
    // click from drag, so panning still works as expected.
    instance.on('click', (event) => {
      if (handlers.current.onDraftMove) {
        handlers.current.onDraftMove({ lat: event.latlng.lat, lng: event.latlng.lng });
      } else if (handlers.current.onMapClick) {
        handlers.current.onMapClick({ lat: event.latlng.lat, lng: event.latlng.lng });
      } else {
        handlers.current.onSelectReport?.(null);
      }
    });

    map.current = instance;
    if (mapRef) mapRef.current = instance;
    emitBounds();

    const reflow = () => instance.invalidateSize();
    window.addEventListener('resize', reflow);
    window.addEventListener('orientationchange', reflow);
    requestAnimationFrame(reflow);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', reflow);
      window.removeEventListener('orientationchange', reflow);
      instance.remove();
      map.current = null;
      markers.current.clear();
    };
    // Intentionally empty: the map is created once and never rebuilt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- diff markers against the fetched list -------------------------------
  useEffect(() => {
    if (!map.current || !clusterGroup.current) return;

    const seen = new Set();
    const toAdd = [];

    for (const report of reports) {
      seen.add(report.id);
      const existing = markers.current.get(report.id);

      if (!existing) {
        const marker = L.marker([report.lat, report.lng], {
          icon: markerIcon(report, report.id === selectedId),
          keyboard: true,
          alt: i18n.t('map.markerAlt', {
            name: localizeCategoryName(report.category_slug, getActiveLocale()) ?? report.category_name,
            count: report.corroboration_count ?? 0,
          }),
          riseOnHover: true,
        });
        marker.on('click', (event) => {
          L.DomEvent.stopPropagation(event);
          handlers.current.onSelectReport?.(report.id);
        });
        // Keyboard users get the same affordance as pointer users.
        marker.on('keypress', (event) => {
          if (event.originalEvent.key === 'Enter' || event.originalEvent.key === ' ') {
            handlers.current.onSelectReport?.(report.id);
          }
        });
        markers.current.set(report.id, { marker, report });
        toAdd.push(marker);

        // Snapped reports render as a soft radius, never a sharp pin, so the
        // map does not imply precision that is not there (plan.md §3).
        if (report.location_precision_m > 0) {
          L.circle([report.lat, report.lng], {
            radius: report.location_precision_m,
            className: 'precision-halo',
            pane: 'precision',
            interactive: false,
          }).addTo(precisionLayer.current);
        }
      } else {
        // Only touch the DOM when something visible actually changed —
        // corroboration counts and heat drift on every refetch otherwise.
        const before = existing.report;
        if (before.corroboration_count !== report.corroboration_count
            || before.expires_at !== report.expires_at
            || (before.id === selectedId) !== (report.id === selectedId)) {
          existing.marker.setIcon(markerIcon(report, report.id === selectedId));
        }
        existing.report = report;
      }
    }

    const removed = [];
    for (const [id, entry] of markers.current) {
      if (!seen.has(id)) {
        removed.push(entry.marker);
        markers.current.delete(id);
      }
    }

    if (toAdd.length) clusterGroup.current.addLayers(toAdd);
    if (removed.length) clusterGroup.current.removeLayers(removed);

    // Halos are cheap and stateless; rebuild them when anything was removed.
    if (removed.length) {
      precisionLayer.current.clearLayers();
      for (const { report } of markers.current.values()) {
        if (report.location_precision_m > 0) {
          L.circle([report.lat, report.lng], {
            radius: report.location_precision_m,
            className: 'precision-halo',
            pane: 'precision',
            interactive: false,
          }).addTo(precisionLayer.current);
        }
      }
    }
  }, [reports, selectedId]);

  // --- selection highlight --------------------------------------------------
  useEffect(() => {
    for (const [id, { marker, report }] of markers.current) {
      marker.setIcon(markerIcon(report, id === selectedId));
    }
  }, [selectedId]);

  // --- imperative recentre (deep links, "locate me", leaderboard links) ------
  useEffect(() => {
    if (!map.current || !flyTo) return;
    const zoom = flyTo.zoom ?? map.current.getZoom();

    // The viewport is bracketed by occluders — the topbar above (padTop) and,
    // when a sheet is open, the composer below (padBottom). Centring on the
    // target hides it behind one of them, so shift the centre so the target
    // lands at the midpoint of the visible band: (padBottom - padTop) / 2 south
    // of it in projected pixels. Done at the destination zoom, since pixel
    // offsets are zoom-dependent.
    let target = L.latLng(flyTo.lat, flyTo.lng);
    const padTop = flyTo.padTop ?? 0;
    const padBottom = flyTo.padBottom ?? 0;
    if ((padTop || padBottom) && padTop + padBottom < map.current.getSize().y) {
      const point = map.current.project(target, zoom);
      target = map.current.unproject(
        [point.x, point.y + (padBottom - padTop) / 2],
        zoom,
      );
    }

    map.current.flyTo(target, zoom, { duration: flyTo.instant ? 0 : 0.8 });
  }, [flyTo]);

  // --- the draft pin while composing a report -------------------------------
  useEffect(() => {
    if (!map.current) return;

    if (!draftPin) {
      draftMarker.current?.remove();
      draftCircle.current?.remove();
      draftMarker.current = null;
      draftCircle.current = null;
      return;
    }

    if (!draftMarker.current) {
      draftMarker.current = L.marker([draftPin.lat, draftPin.lng], {
        draggable: true,
        icon: L.divIcon({
          className: 'draft-marker-wrapper',
          html: '<div class="draft-marker"></div>',
          iconSize: [34, 34],
          iconAnchor: [17, 17],
        }),
        zIndexOffset: 1000,
      }).addTo(map.current);

      draftMarker.current.on('dragend', (event) => {
        const { lat, lng } = event.target.getLatLng();
        handlers.current.onDraftMove?.({ lat, lng });
      });
    } else {
      draftMarker.current.setLatLng([draftPin.lat, draftPin.lng]);
    }

    // Once a category is chosen, the pin shows the precision that will
    // actually be stored, so the user is not misled about what they are
    // publishing (plan.md §4.2).
    const precision = draftPin.precisionM ?? 0;
    if (precision > 0) {
      if (!draftCircle.current) {
        draftCircle.current = L.circle([draftPin.lat, draftPin.lng], {
          radius: precision,
          className: 'draft-precision-halo',
          interactive: false,
        }).addTo(map.current);
      } else {
        draftCircle.current.setLatLng([draftPin.lat, draftPin.lng]);
        draftCircle.current.setRadius(precision);
      }
    } else {
      draftCircle.current?.remove();
      draftCircle.current = null;
    }
  }, [draftPin]);

  // --- the pending "tap-to-place" pin --------------------------------------
  // Distinct from the draft pin: this is the marker shown BEFORE the composer
  // opens, with a popup that lets the user confirm the location.  Dragging
  // the marker updates App state; tapping the popup button promotes it to a
  // draft and opens the composer (handled in App).
  useEffect(() => {
    if (!map.current) return;

    if (!pinDrop) {
      pinDropMarker.current?.remove();
      pinDropMarker.current = null;
      return;
    }

    const onButton = (event) => {
      L.DomEvent.stopPropagation(event);
      L.DomEvent.preventDefault(event);
      handlers.current.onConfirmPinDrop?.();
    };
    const onCancel = (event) => {
      L.DomEvent.stopPropagation(event);
      L.DomEvent.preventDefault(event);
      handlers.current.onCancelPinDrop?.();
    };

    if (!pinDropMarker.current) {
      const marker = L.marker([pinDrop.lat, pinDrop.lng], {
        draggable: true,
        icon: L.divIcon({
          className: 'pin-drop-wrapper',
          html: '<div class="pin-drop"></div>',
          iconSize: [34, 34],
          iconAnchor: [17, 17],
        }),
        zIndexOffset: 900,
      }).addTo(map.current);

      marker.on('dragend', (event) => {
        const { lat, lng } = event.target.getLatLng();
        handlers.current.onPinDropMove?.({ lat, lng });
      });

      marker.bindPopup(
        () => {
          const root = document.createElement('div');
          root.className = 'pin-drop-popup';
          root.innerHTML = `
            <button type="button" class="pin-drop-popup__confirm">${i18n.t('map.reportHere')}</button>
            <button type="button" class="pin-drop-popup__cancel" aria-label="${i18n.t('map.dismiss')}"></button>`;
          root.querySelector('.pin-drop-popup__confirm')
            .addEventListener('click', onButton);
          root.querySelector('.pin-drop-popup__cancel')
            .addEventListener('click', onCancel);
          return root;
        },
        { closeOnClick: false, closeButton: false, autoClose: false, className: 'pin-drop-leaflet-popup', offset: [0, 12] },
      );
      marker.openPopup();
      pinDropMarker.current = marker;
    } else {
      pinDropMarker.current.setLatLng([pinDrop.lat, pinDrop.lng]);
    }
  }, [pinDrop]);

  return (
    <div
      ref={containerRef}
      className="map"
      role="application"
      aria-label={i18n.t('map.aria')}
    />
  );
}
